import { v4 as uuidv4 } from 'uuid';
import { action, computed, observable } from 'mobx';
import { message } from 'antd';
import { DataCrafterTemplatesService } from '../services';
import { DataCrafterTemplateColumn, DataCrafterOutputVariant, JobStatus, ScriptGenerationResponse } from '../types';
import { throttle } from 'lodash';
import CodeValidationContext from '../types/CodeValidationContext';
import { TestPickerScriptResponse } from '../types';
import { Utils } from '../../common/services/Utils';

/**
 * Interface for tracking compilation progress during script generation
 */
interface CompileProgress {
    step: number;
    total_steps: number;
    message: string;
    status: string;
    duration: number;
    step_details?: {
        model_output?: string;
        observations?: string;
        action_output?: string | null;
        tool_calls?: Array<unknown>;
    };
}

/**
 * Interface for managing template compilation state
 */
interface TemplateCompilationState {
    templateScript: string | null;
    templateScriptRefId: string | null;
    isLoadingTemplateScript: boolean;
    compilationProgress: CompileProgress | null;
    templateScriptError: string | undefined;
}

/**
 * Default state for template compilation
 */
const defaultTemplateCompilationState: TemplateCompilationState = {
    templateScript: null,
    templateScriptRefId: null,
    isLoadingTemplateScript: false,
    compilationProgress: null,
    templateScriptError: undefined
} as const;

/**
 * Store responsible for managing script compilation functionality
 * Handles both template script compilation and variant picker script compilation
 */
export default class DataCrafterScriptCompilationStore {
    // ========== Observable Properties ==========

    @observable
    isPreviewLoading: boolean = false;

    @observable
    isValidateLoading: boolean = false;

    @observable
    isTestingPickerScript: boolean = false;

    @observable
    pickerScriptTestResult: TestPickerScriptResponse | null = null;

    @observable
    codeValidationContext: CodeValidationContext | undefined = undefined;

    // Temp id of the template that is being created (used to store the compilation state)
    @observable
    tempTemplateId: string = this.generateTempId();

    @observable
    private templateCompilationStates = observable.map<string, TemplateCompilationState>();

    @observable
    private templateVariantPickerCompilationStates = observable.map<string, TemplateCompilationState>();

    private readonly tempTemplateIdPrefix = 'temp-template-';
    private readonly templateJobPollingDelay = 1000;
    private readonly previewFileName = 'preview.xlsx';

    // ========== Constructor ==========

    constructor(private dataCrafterTemplatesService: DataCrafterTemplatesService) {}

    // ========== Computed Properties ==========

    @computed
    get currentTemplateCompilationState(): TemplateCompilationState {
        return this.templateCompilationStates.get(this.tempTemplateId) ?? { ...defaultTemplateCompilationState };
    }

    @computed
    get currentTemplateVariantPickerCompilationState(): TemplateCompilationState {
        return (
            this.templateVariantPickerCompilationStates.get(this.tempTemplateId) ?? {
                ...defaultTemplateCompilationState
            }
        );
    }

    @computed
    get currentTemplateScript(): string | null {
        return this.currentTemplateCompilationState.templateScript;
    }

    @computed
    get currentTemplateScriptRefId(): string | null {
        return this.currentTemplateCompilationState.templateScriptRefId;
    }

    @computed
    get currentTemplateScriptError(): string | undefined {
        return this.currentTemplateCompilationState.templateScriptError;
    }

    @computed
    get currentCompilationProgress(): CompileProgress | null {
        return this.currentTemplateCompilationState.compilationProgress;
    }

    @computed
    get isCurrentTemplateLoading(): boolean {
        return this.currentTemplateCompilationState.isLoadingTemplateScript;
    }

    @computed
    get isCurrentTemplateVariantPickerLoading(): boolean {
        return this.currentTemplateVariantPickerCompilationState.isLoadingTemplateScript;
    }

    @computed
    get isBusyWithScriptOperation(): boolean {
        return (
            this.isCurrentTemplateLoading ||
            this.isPreviewLoading ||
            this.isValidateLoading ||
            this.isTestingPickerScript ||
            this.isCurrentTemplateVariantPickerLoading
        );
    }

    // ========== Actions ==========

    @action.bound
    generateTempTemplateId() {
        this.tempTemplateId = this.generateTempId();
    }

    @action.bound
    setCodeValidationContext(context: CodeValidationContext | undefined) {
        this.codeValidationContext = context;
    }

    @action.bound
    setIsPreviewLoading(isLoading: boolean) {
        this.isPreviewLoading = isLoading;
    }

    @action.bound
    setIsValidateLoading(isLoading: boolean) {
        this.isValidateLoading = isLoading;
    }

    @action.bound
    setIsTestingPickerScript(isLoading: boolean) {
        this.isTestingPickerScript = isLoading;
    }

    @action.bound
    setPickerScriptTestResult(result: TestPickerScriptResponse | null) {
        this.pickerScriptTestResult = result;
    }

    @action.bound
    setTemplateScriptError(templateId: string, error: string | undefined) {
        this.setTemplateCompilationState(templateId, { templateScriptError: error });
    }

    @action.bound
    setTemplateCompilationState(templateId: string, state: Partial<TemplateCompilationState>) {
        const currentState = this.templateCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
        this.templateCompilationStates.set(templateId, {
            ...currentState,
            ...state
        });
    }

    @action.bound
    setTemplateVariantPickerCompilationState(templateId: string, state: Partial<TemplateCompilationState>) {
        const currentState = this.templateVariantPickerCompilationStates.get(templateId) ?? {
            ...defaultTemplateCompilationState
        };
        this.templateVariantPickerCompilationStates.set(templateId, {
            ...currentState,
            ...state
        });
    }

    @action.bound
    setCurrentTemplateId(templateId: string) {
        this.tempTemplateId = templateId;
    }

    // ========== Script Compilation Methods ==========

    @action.bound
    async runTemplateScriptJob(
        projectId: string,
        templateId: string,
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        templateScriptRefId: string | null,
        outputVariants: DataCrafterOutputVariant[],
        variantPickerScriptRefId?: string | null,
        selectedVariantId?: string
    ): Promise<void> {
        try {
            this.setIsPreviewLoading(true);
            this.setTemplateScriptError(this.tempTemplateId, undefined);

            const job = await this.dataCrafterTemplatesService.createTemplateScriptRunJob(
                projectId,
                templateId,
                selectedInputPackages,
                selectedOutputPackage,
                templateScriptRefId,
                outputVariants.map(variant => ({
                    variantId: variant.variantId,
                    name: variant.name,
                    condition: variant.condition,
                    isDefault: variant.isDefault,
                    scriptRefId: variant.scriptRefId
                })),
                variantPickerScriptRefId || null,
                selectedVariantId
            );

            const blob = await this.waitForAsyncTemplateResult(job.jobId);
            Utils.downloadBlob(blob, this.previewFileName);
            message.success('Template script preview generated successfully');
        } catch (error) {
            console.error('Failed to run template script:', error);
            const errorMessage = (error as Error).message || 'Unknown error';
            message.error('Failed to run template script: ' + errorMessage);
            this.setTemplateScriptError(this.tempTemplateId, 'Failed to run template script: ' + errorMessage);
        } finally {
            this.setIsPreviewLoading(false);
        }
    }

    @action.bound
    async validateTemplateScript(
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        templateScript: string,
        outputColumns: DataCrafterTemplateColumn[],
        modelId: string
    ): Promise<void> {
        try {
            this.setTemplateScriptError(this.tempTemplateId, undefined);
            this.setCodeValidationContext(undefined);
            this.setIsValidateLoading(true);

            const response = await this.dataCrafterTemplatesService.validateTemplateScript(
                selectedInputPackages,
                selectedOutputPackage,
                templateScript,
                outputColumns.filter(col => col.mappingRule),
                modelId
            );

            const context = new CodeValidationContext(response.code_analysis_result, response.rule_analysis);
            this.setCodeValidationContext(context);

            message.success('Script validation completed successfully');
        } catch (error) {
            console.error('Failed to validate template script:', error);
            message.error('Failed to validate template script');
            this.setTemplateScriptError(
                this.tempTemplateId,
                'Failed to validate template script: ' + ((error as Error).message || 'Unknown error')
            );
        } finally {
            this.setIsValidateLoading(false);
        }
    }

    @action.bound
    async compileTemplateScriptWithProgress(
        selectedInputPackages: string[],
        selectedOutputPackage: string,
        outputColumns: DataCrafterTemplateColumn[],
        plannerModelId: string | null,
        coderModelId: string | null,
        templateId: string,
        projectId: string,
        selectedOutputVariant?: DataCrafterOutputVariant
    ): Promise<void> {
        try {
            this.setTemplateCompilationState(templateId, {
                isLoadingTemplateScript: true,
                compilationProgress: {
                    step: 1,
                    total_steps: 1,
                    message:
                        'Initializing code generation...\nProcessing template configuration and preparing initial prompt.\n',
                    status: 'in_progress',
                    duration: 0
                },
                templateScriptError: undefined,
                templateScript: selectedOutputVariant ? this.currentTemplateScript : undefined
            });

            // throttle streaming updates to batch rapid messages
            let streamingBuffer = '';
            const updateStreamMessage = throttle((msg: string) => {
                const currentState = this.templateCompilationStates.get(templateId) ?? {
                    ...defaultTemplateCompilationState
                };
                this.setTemplateCompilationState(templateId, {
                    compilationProgress: {
                        ...currentState.compilationProgress!,
                        message: msg
                    }
                });
            }, 100);

            await this.dataCrafterTemplatesService.compileTemplateScriptStream(
                selectedInputPackages,
                selectedOutputPackage,
                outputColumns.filter(col => col.mappingRule),
                plannerModelId,
                coderModelId,
                projectId,
                templateId.startsWith(this.tempTemplateIdPrefix) ? null : templateId,
                {
                    onProgress: progress => {
                        this.setTemplateCompilationState(templateId, {
                            compilationProgress: progress
                        });
                    },
                    onStreaming: delta => {
                        // buffer deltas and trigger throttled update
                        streamingBuffer += delta;
                        updateStreamMessage(streamingBuffer);
                    },
                    onComplete: async (scriptRefId, code) => {
                        // cancel any pending throttled updates
                        updateStreamMessage.cancel();

                        const currentState = this.getTemplateCompilationState(templateId);
                        this.setTemplateCompilationState(templateId, {
                            templateScript: selectedOutputVariant
                                ? currentState.templateScript
                                : (code ?? currentState.templateScript),
                            templateScriptRefId: selectedOutputVariant ? currentState.templateScriptRefId : scriptRefId,
                            isLoadingTemplateScript: false,
                            compilationProgress: null
                        });

                        if (selectedOutputVariant) {
                            selectedOutputVariant.scriptRefId = scriptRefId;
                            selectedOutputVariant.code = code ?? selectedOutputVariant.code;
                        }
                        // Note: Template update is handled by the form store
                    },
                    onError: error => {
                        // cancel any pending throttled updates
                        updateStreamMessage.cancel();
                        this.setTemplateCompilationState(templateId, {
                            templateScriptError: `Failed to compile template script: ${error.message}`,
                            isLoadingTemplateScript: false,
                            compilationProgress: null,
                            templateScript: selectedOutputVariant ? this.currentTemplateScript : undefined
                        });
                    }
                }
            );
        } catch (error) {
            console.error('Failed to compile template script:', error);
            this.setTemplateCompilationState(templateId, {
                templateScriptError: `Failed to compile template script: ${error.message}`,
                isLoadingTemplateScript: false,
                compilationProgress: null
            });
        }
    }

    @action.bound
    async compileTemplateVariantPickerScriptWithProgress(
        outputVariants: DataCrafterOutputVariant[],
        coderModelId: string,
        templateId: string,
        projectId: string
    ): Promise<void> {
        try {
            // Clear previous test result since compilation will generate new script
            this.setPickerScriptTestResult(null);

            this.setTemplateVariantPickerCompilationState(templateId, {
                isLoadingTemplateScript: true,
                compilationProgress: {
                    step: 1,
                    total_steps: 1,
                    message:
                        'Initializing code generation...\nProcessing template configuration and preparing initial prompt.\n',
                    status: 'in_progress',
                    duration: 0
                },
                templateScriptError: undefined
            });

            const response: ScriptGenerationResponse =
                await this.dataCrafterTemplatesService.compileTemplateVariantPickerScript(
                    outputVariants.map(variant => ({
                        variantId: variant.variantId,
                        name: variant.name,
                        condition: variant.condition,
                        isDefault: variant.isDefault,
                        scriptRefId: variant.scriptRefId
                    })),
                    coderModelId,
                    projectId,
                    templateId.startsWith(this.tempTemplateIdPrefix) ? undefined : templateId
                );

            this.setTemplateVariantPickerCompilationState(templateId, {
                templateScript:
                    response.code ?? this.getTemplateVariantPickerCompilationState(templateId).templateScript,
                templateScriptRefId: response.scriptRefId || null,
                isLoadingTemplateScript: false,
                compilationProgress: null
            });
        } catch (error) {
            console.error('Failed to compile picker script:', error);
            this.setTemplateVariantPickerCompilationState(templateId, {
                templateScriptError: `Failed to compile picker script: ${error.message}`,
                isLoadingTemplateScript: false,
                compilationProgress: null
            });
        }
    }

    @action.bound
    async testTemplateVariantPickerScript(
        projectId: string,
        templateId: string,
        selectedInputPackages: string[],
        pickerScriptRefId: string,
        outputVariants: DataCrafterOutputVariant[]
    ): Promise<void> {
        try {
            this.setIsTestingPickerScript(true);
            this.setPickerScriptTestResult(null);

            const response = await this.dataCrafterTemplatesService.testTemplateVariantPickerScript(
                projectId,
                templateId,
                selectedInputPackages,
                pickerScriptRefId,
                outputVariants.map(variant => ({
                    variantId: variant.variantId,
                    name: variant.name,
                    condition: variant.condition,
                    isDefault: variant.isDefault,
                    scriptRefId: variant.scriptRefId
                }))
            );

            this.setPickerScriptTestResult(response);
        } catch (error) {
            console.error('Failed to test picker script:', error);
            message.error('Failed to test picker script');
        } finally {
            this.setIsTestingPickerScript(false);
        }
    }

    // ========== Utility Methods ==========

    @action.bound
    reset() {
        // Reset loading states
        this.isPreviewLoading = false;
        this.isValidateLoading = false;
        this.isTestingPickerScript = false;

        // Reset picker script test result
        this.pickerScriptTestResult = null;

        // Reset compilation states for temp templates
        this.templateCompilationStates.forEach((_, key) => {
            if (key.startsWith(this.tempTemplateIdPrefix)) {
                this.templateCompilationStates.delete(key);
            }
        });

        this.templateVariantPickerCompilationStates.forEach((_, key) => {
            if (key.startsWith(this.tempTemplateIdPrefix)) {
                this.templateVariantPickerCompilationStates.delete(key);
            }
        });

        this.setCodeValidationContext(undefined);
    }

    @action.bound
    initializeWithTemplate(templateId: string, templateCode?: string, variantPickerScript?: string) {
        this.setCurrentTemplateId(templateId);

        this.setTemplateCompilationState(templateId, {
            templateScript: this.currentTemplateCompilationState.templateScript || templateCode,
            templateScriptRefId: null
        });

        this.setTemplateVariantPickerCompilationState(templateId, {
            templateScript: variantPickerScript || undefined,
            templateScriptRefId: null
        });
    }

    /**
     * Get compilation state for a specific template
     */
    getTemplateCompilationState(templateId: string): TemplateCompilationState {
        return this.templateCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
    }

    /**
     * Get variant picker compilation state for a specific template
     */
    getTemplateVariantPickerCompilationState(templateId: string): TemplateCompilationState {
        return this.templateVariantPickerCompilationStates.get(templateId) ?? { ...defaultTemplateCompilationState };
    }

    /**
     * Get the display script for a template (considering output variants)
     */
    getDisplayTemplateScript(templateId: string, selectedOutputVariant?: DataCrafterOutputVariant): string | null {
        return selectedOutputVariant?.code ?? this.getTemplateCompilationState(templateId).templateScript;
    }

    getTemplateScriptRefId(templateId: string, selectedOutputVariant?: DataCrafterOutputVariant): string | null {
        return selectedOutputVariant?.scriptRefId ?? this.getTemplateCompilationState(templateId).templateScriptRefId;
    }

    getVariantPickerScriptRefId(templateId: string): string | null {
        return this.getTemplateVariantPickerCompilationState(templateId).templateScriptRefId;
    }

    private async waitForAsyncTemplateResult(jobId: string): Promise<Blob> {
        let job = await this.dataCrafterTemplatesService.getTemplateScriptRunJob(jobId);

        while (job.status !== JobStatus.Completed && job.status !== JobStatus.Failed) {
            await this.delay(this.templateJobPollingDelay);
            job = await this.dataCrafterTemplatesService.getTemplateScriptRunJob(jobId);
        }

        if (job.status === JobStatus.Failed) {
            throw new Error(job.error ?? 'Template script run job failed');
        }

        return this.dataCrafterTemplatesService.downloadTemplateScriptRunJobResult(jobId);
    }

    private async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private generateTempId(): string {
        return `${this.tempTemplateIdPrefix}${uuidv4()}`;
    }
}
